/** Shared contact for legal / security inquiries */
export const LEGAL_CONTACT_EMAIL = 'exportnook@gmail.com';

/** @type {Record<string, { title: string; lastUpdated: string; sections: Array<{ heading: string; body: string[] }> }>} */
export const LEGAL_POLICIES = {
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: '5 June 2026',
    sections: [
      {
        heading: '1. Introduction',
        body: [
          'ExportNook (“we”, “us”, “our”) operates the export management platform at exportnook.com and related services (the “Service”). This Privacy Policy explains how we collect, use, disclose, and protect personal information when you use the Service.',
          'By creating an account or using ExportNook, you agree to the practices described in this policy. If you do not agree, please do not use the Service.',
        ],
      },
      {
        heading: '2. Information we collect',
        body: [
          'Account information: name, email address, phone number, company details, GSTIN, IEC, bank and AD code details, and credentials you provide during registration or profile setup.',
          'Business and export data: invoices, packing lists, shipping bills, IRM/FIRA records, address books, courier bookings, and other documents you enter or upload for export operations.',
          'Integration data: credentials and configuration for third-party services you connect (for example courier APIs, India Post CEPT, Amazon integrations), stored securely and used only to provide requested features.',
          'Technical data: IP address, browser type, device information, session logs, authentication events, and usage metadata needed to operate, secure, and improve the Service.',
          'Communications: messages you send to support and records of security or account-related notifications we send to you.',
        ],
      },
      {
        heading: '3. How we use information',
        body: [
          'Provide, maintain, and improve the Service, including document generation, reconciliation, courier booking, and integrations you enable.',
          'Authenticate users, enforce access controls, prevent fraud and abuse, and comply with legal obligations.',
          'Send transactional messages (account confirmation, password reset, security alerts) and respond to support requests.',
          'Analyze aggregated, de-identified usage to improve performance and user experience.',
          'We do not sell your personal information to third parties.',
        ],
      },
      {
        heading: '4. Legal basis and compliance',
        body: [
          'We process personal data to perform our contract with you, to comply with applicable law (including export and tax record-keeping requirements where relevant), and based on our legitimate interests in securing and improving the Service.',
          'Where required, we obtain consent for specific processing activities such as optional marketing communications.',
        ],
      },
      {
        heading: '5. Sharing and disclosure',
        body: [
          'Service providers: hosting, email delivery, security monitoring, and infrastructure partners that process data on our behalf under contractual confidentiality and security obligations.',
          'Courier and integration partners: only when you initiate a booking or connection and only the data required for that transaction.',
          'Legal requirements: when required by law, regulation, court order, or to protect the rights, property, or safety of ExportNook, our users, or others.',
          'Business transfers: in connection with a merger, acquisition, or sale of assets, subject to continued protection consistent with this policy.',
        ],
      },
      {
        heading: '6. Data retention',
        body: [
          'We retain account and business data for as long as your account is active and as needed to provide the Service. Specific retention periods for backups, logs, and deleted accounts are described in our Data Retention & Deletion Policy.',
        ],
      },
      {
        heading: '7. Security',
        body: [
          'We implement administrative, technical, and organizational measures to protect personal information. Details are outlined in our Security Policy. No method of transmission or storage is completely secure; we encourage strong passwords and enabling two-factor authentication where available.',
        ],
      },
      {
        heading: '8. Your rights',
        body: [
          'Depending on applicable law, you may request access, correction, deletion, restriction, or portability of your personal data, or object to certain processing.',
          'You may update much of your profile information directly in the application. For other requests, contact us at the email below. We may verify your identity before fulfilling a request.',
        ],
      },
      {
        heading: '9. Cookies and similar technologies',
        body: [
          'We use cookies and local storage for authentication sessions, security (including CAPTCHA), and essential application functionality. We do not use third-party advertising cookies on the authenticated application.',
        ],
      },
      {
        heading: '10. International transfers',
        body: [
          'Your data may be processed in India and, where we use cloud infrastructure, in other countries with appropriate safeguards consistent with applicable data protection requirements.',
        ],
      },
      {
        heading: '11. Children',
        body: [
          'ExportNook is intended for business users and is not directed at children under 18. We do not knowingly collect personal information from children.',
        ],
      },
      {
        heading: '12. Changes to this policy',
        body: [
          'We may update this Privacy Policy from time to time. Material changes will be posted on this page with an updated “Last updated” date. Continued use of the Service after changes constitutes acceptance of the revised policy.',
        ],
      },
      {
        heading: '13. Contact',
        body: [
          `Privacy-related questions or requests: ${LEGAL_CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  security: {
    title: 'Security Policy',
    lastUpdated: '5 June 2026',
    sections: [
      {
        heading: '1. Purpose',
        body: [
          'This Security Policy describes how ExportNook protects customer data and platform integrity. It applies to the exportnook.com application, APIs, and supporting infrastructure.',
        ],
      },
      {
        heading: '2. Security principles',
        body: [
          'Defense in depth: multiple layers of controls across network, application, and data layers.',
          'Least privilege: access to production systems and customer data is limited to authorized personnel on a need-to-know basis.',
          'Secure by default: authentication is required for business data; sensitive operations support additional verification where implemented (for example two-factor authentication).',
          'Continuous improvement: we review and update controls as threats and the Service evolve.',
        ],
      },
      {
        heading: '3. Access control and authentication',
        body: [
          'User accounts are protected with password requirements and optional two-factor authentication (TOTP).',
          'Session tokens are issued using industry-standard mechanisms; sessions expire after a defined period of inactivity.',
          'Administrative access to production environments requires separate credentials and is logged.',
          'Access to Amazon Information is granted only on a documented need-to-know basis and limited to personnel whose job responsibilities require such access.',
        ],
      },
      {
        heading: '4. Data protection',
        body: [
          'Data in transit is encrypted using TLS (HTTPS) for browser and API communication.',
          'Passwords are stored using strong one-way hashing (bcrypt); we do not store plaintext passwords.',
          'Production databases, backups, and file storage systems use encryption at rest. Sensitive data is protected using industry-standard encryption mechanisms such as AES-256 where supported by the underlying platform and storage provider.',
          'Customer business records are scoped per account; application logic enforces user-level data isolation.',
        ],
      },
      {
        heading: '5. Application security',
        body: [
          'Input validation and parameterized queries are used to reduce injection and data integrity risks.',
          'Authentication is enforced on API endpoints that access customer data.',
          'Third-party integrations (couriers, postal APIs, marketplaces) use credentials you provide; we proxy requests where possible so secrets are not exposed in the browser beyond what the integration requires.',
          'Security verification (CAPTCHA) is used on selected public endpoints to reduce automated abuse.',
        ],
      },
      {
        heading: '6. Infrastructure and operations',
        body: [
          'The Service runs in containerized environments with network segmentation between application and database tiers.',
          'Security patches are applied to operating systems and dependencies on a regular schedule.',
          'Logs are retained for security monitoring, troubleshooting, and audit purposes as described in our Data Retention & Deletion Policy.',
        ],
      },
      {
        heading: '7. Vendor management',
        body: [
          'Sub-processors (hosting, email, infrastructure) are selected with regard to security practices and bound by contractual obligations to protect data and use it only to provide services to ExportNook.',
        ],
      },
      {
        heading: '8. Employee and contractor access',
        body: [
          'Personnel with access to production systems undergo background checks where appropriate and receive security awareness guidance.',
          'Access is revoked promptly when no longer required.',
        ],
      },
      {
        heading: '9. Vulnerability management',
        body: [
          'Security vulnerabilities identified through code review, dependency scanning, automated security monitoring, user reports, or external assessments are documented and tracked until remediation is completed. Remediation priority is assigned according to severity and risk level.',
        ],
      },
      {
        heading: '10. Incident response',
        body: [
          'Security incidents are handled according to our Incident Response Policy. Users may report suspected vulnerabilities or incidents to the contact below.',
        ],
      },
      {
        heading: '11. Responsible disclosure',
        body: [
          'If you believe you have discovered a security vulnerability in ExportNook, please report it responsibly to the contact below. Do not publicly disclose issues until we have had a reasonable opportunity to investigate and remediate.',
        ],
      },
      {
        heading: '12. Contact',
        body: [
          `Security inquiries and vulnerability reports: ${LEGAL_CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  'data-retention': {
    title: 'Data Retention & Deletion Policy',
    lastUpdated: '5 June 2026',
    sections: [
      {
        heading: '1. Purpose',
        body: [
          'This policy explains how long ExportNook retains different categories of data and how account holders can request deletion.',
        ],
      },
      {
        heading: '2. Active accounts',
        body: [
          'While your account is active, we retain the business data you create (invoices, packing lists, IRM records, address books, courier metadata, and related documents) so you can access, edit, and export it through the Service.',
          'Profile and integration settings are retained for as long as needed to operate connected features.',
        ],
      },
      {
        heading: '3. Backups',
        body: [
          'Database and file backups may retain copies of deleted data for a limited period (typically up to 30 days) for disaster recovery. Backups are overwritten on a rolling schedule and are not used to restore individual records except where required for recovery operations.',
          'Backups are stored in geographically separate locations. The target Recovery Point Objective (RPO) is 24 hours and the target Recovery Time Objective (RTO) is 8 hours.',
        ],
      },
      {
        heading: '4. Logs and security records',
        body: [
          'Authentication logs, API access logs, administrative access logs, and security event records are retained for a minimum of 12 months. Logs are reviewed at least every two weeks for suspicious activity, unauthorized access attempts, and security incidents.',
          'Aggregated analytics without personal identifiers may be retained longer for service improvement.',
        ],
      },
      {
        heading: '5. Account deletion',
        body: [
          'You may request deletion of your account from Profile settings or by contacting us. Account deletion permanently removes your user account and associated business data from active production systems, subject to the backup retention window described above.',
          'Deletion is irreversible. Export any records you need before requesting deletion.',
          'We may retain minimal information where required by law (for example tax, export compliance, or dispute resolution) for the period mandated by applicable regulations.',
        ],
      },
      {
        heading: '6. Inactive accounts',
        body: [
          'Accounts with no sign-in activity for an extended period may be flagged for review. We may contact the registered email before taking action on long-inactive accounts.',
        ],
      },
      {
        heading: '7. Third-party integrations',
        body: [
          'Data transmitted to courier, postal, or marketplace APIs is subject to those providers’ retention policies once sent. Disconnect integrations and delete related credentials in Profile when no longer needed.',
        ],
      },
      {
        heading: '8. Data export',
        body: [
          'Where available in the application, you may export your data (for example backup or document export features) before deletion. Contact support if you need assistance exporting records.',
        ],
      },
      {
        heading: '9. Contact',
        body: [
          `Retention or deletion requests: ${LEGAL_CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  'incident-response': {
    title: 'Incident Response Policy',
    lastUpdated: '5 June 2026',
    sections: [
      {
        heading: '1. Purpose',
        body: [
          'This Incident Response Policy defines how ExportNook detects, responds to, and communicates about security incidents that may affect the confidentiality, integrity, or availability of the Service or customer data.',
        ],
      },
      {
        heading: '2. Scope',
        body: [
          'This policy covers security incidents affecting ExportNook systems, customer data stored on our platform, and integrations operated through our infrastructure. It applies to employees, contractors, and authorized service providers involved in incident handling.',
        ],
      },
      {
        heading: '3. Incident categories',
        body: [
          'Unauthorized access to accounts or systems.',
          'Suspected data breach or exfiltration of customer information.',
          'Malware, ransomware, or denial-of-service affecting the Service.',
          'Loss or theft of devices or credentials with access to production systems.',
          'Vulnerabilities actively exploited in the wild against ExportNook.',
        ],
      },
      {
        heading: '4. Detection and reporting',
        body: [
          'Incidents may be detected through monitoring, automated alerts, user reports, or responsible disclosure.',
          'Users who suspect unauthorized access to their account should reset their password, enable two-factor authentication if available, and contact us immediately.',
          `Report security incidents or vulnerabilities to ${LEGAL_CONTACT_EMAIL} with a description, time observed, and any relevant logs or screenshots.`,
        ],
      },
      {
        heading: '5. Response process',
        body: [
          'Triage: confirm the incident, assess severity, and assign an incident lead.',
          'Containment: limit further impact (for example revoke compromised credentials, block malicious traffic, isolate affected components).',
          'Investigation: determine root cause, scope of affected data and users, and timeline of events.',
          'Eradication and recovery: remove malicious access, apply fixes, restore services from clean backups if needed, and verify system integrity.',
          'Post-incident review: document lessons learned and implement preventive improvements.',
        ],
      },
      {
        heading: '6. Severity levels',
        body: [
          'Critical: active breach of customer data or complete service outage — immediate response, 24×7 engagement until contained.',
          'High: significant service degradation or credible threat to data — response within hours.',
          'Medium: limited impact or contained issue — response within one business day.',
          'Low: minor issue with no customer data impact — scheduled remediation.',
        ],
      },
      {
        heading: '7. Notification',
        body: [
          'We will notify affected users and regulators where required by applicable law without undue delay after confirming a breach of personal data.',
          'If a security incident involves Amazon Information, ExportNook will notify Amazon at security@amazon.com within 24 hours of detection and provide ongoing updates until resolution.',
          'Notifications will describe the nature of the incident, data potentially affected, steps we are taking, and recommended actions for users (such as password changes).',
          'We will not disclose information that could impede an active investigation or increase risk to users.',
        ],
      },
      {
        heading: '8. Roles and responsibilities',
        body: [
          'The incident lead coordinates technical response, documentation, and communication.',
          'Engineering implements containment and remediation.',
          'Management approves external notifications and regulatory filings where applicable.',
        ],
      },
      {
        heading: '9. Testing and improvement',
        body: [
          'We periodically review incident response procedures and update them based on changes to the Service, threat landscape, and outcomes of post-incident reviews.',
          'This Incident Response Policy is formally reviewed at least every six months and updated when required by changes in infrastructure, services, or regulatory requirements.',
        ],
      },
      {
        heading: '10. Contact',
        body: [
          `Report a security incident: ${LEGAL_CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
};

/** Public routes → policy keys */
export const LEGAL_POLICY_LINKS = [
  { path: '/privacy-policy', key: 'privacy', label: 'Privacy Policy' },
  { path: '/security', key: 'security', label: 'Security Policy' },
  { path: '/data-retention', key: 'data-retention', label: 'Data Retention & Deletion' },
  { path: '/incident-response', key: 'incident-response', label: 'Incident Response' },
];
